import { useCallback, useEffect, useRef, useState } from "react";
import { DriveItem } from "../types";
import { buildUploadUrl, formatSize } from "../helpers";

interface ImageLightboxProps {
  /** Image items the viewer can page through. */
  images: DriveItem[];
  startIndex: number;
  onClose: () => void;
  onDownload: (item: DriveItem) => void;
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 6;
const ZOOM_STEP = 0.3;
// Image is fit within this share of the viewport (never upscaled past natural).
const MAX_W_RATIO = 0.86;
const MAX_H_RATIO = 0.7;

/**
 * Light-themed image viewer rendered as a centered modal that sizes itself to
 * the image (capped to the viewport). Supports scroll / button zoom,
 * drag-to-pan, rotate, and paging across all images in the current view.
 */
const ImageLightbox = ({
  images,
  startIndex,
  onClose,
  onDownload,
}: ImageLightboxProps) => {
  const [index, setIndex] = useState(startIndex);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [loaded, setLoaded] = useState(false);
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null);

  const stageRef = useRef<HTMLDivElement>(null);
  const natural = useRef({ w: 0, h: 0 });
  const drag = useRef({
    active: false,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
  });

  const current = images[index];

  // Displayed size = natural size scaled down to fit the viewport (never up).
  const computeDims = useCallback(() => {
    const { w, h } = natural.current;
    if (!w || !h) return;
    const maxW = window.innerWidth * MAX_W_RATIO;
    const maxH = window.innerHeight * MAX_H_RATIO;
    const scale = Math.min(1, maxW / w, maxH / h);
    setDims({ w: Math.round(w * scale), h: Math.round(h * scale) });
  }, []);

  const reset = useCallback(() => {
    setZoom(1);
    setRotation(0);
    setOffset({ x: 0, y: 0 });
  }, []);

  const goTo = useCallback(
    (next: number) => {
      if (images.length <= 1) return;
      const wrapped = (next + images.length) % images.length;
      setLoaded(false);
      setDims(null);
      natural.current = { w: 0, h: 0 };
      setIndex(wrapped);
      reset();
    },
    [images.length, reset],
  );

  const zoomBy = useCallback((delta: number) => {
    setZoom((z) =>
      Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, +(z + delta).toFixed(2))),
    );
  }, []);

  const rotateBy = useCallback((deg: number) => {
    setRotation((r) => r + deg);
  }, []);

  useEffect(() => {
    if (zoom === 1) setOffset({ x: 0, y: 0 });
  }, [zoom]);

  // Keyboard shortcuts.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") goTo(index + 1);
      else if (e.key === "ArrowLeft") goTo(index - 1);
      else if (e.key === "+" || e.key === "=") zoomBy(ZOOM_STEP);
      else if (e.key === "-" || e.key === "_") zoomBy(-ZOOM_STEP);
      else if (e.key === "0") reset();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, goTo, zoomBy, reset, onClose]);

  // Recompute the fitted size when the window resizes.
  useEffect(() => {
    window.addEventListener("resize", computeDims);
    return () => window.removeEventListener("resize", computeDims);
  }, [computeDims]);

  // Wheel zoom over the image stage (non-passive so we can prevent scroll).
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      zoomBy(e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [zoomBy]);

  // Preload neighbours for snappy paging.
  useEffect(() => {
    [index - 1, index + 1].forEach((i) => {
      const neighbour = images[(i + images.length) % images.length];
      if (neighbour) {
        const img = new Image();
        img.src = buildUploadUrl(neighbour.path);
      }
    });
  }, [index, images]);

  const onMouseDown = (e: React.MouseEvent) => {
    if (zoom <= 1) return;
    drag.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      originX: offset.x,
      originY: offset.y,
    };
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (!drag.current.active) return;
    setOffset({
      x: drag.current.originX + (e.clientX - drag.current.startX),
      y: drag.current.originY + (e.clientY - drag.current.startY),
    });
  };

  const endDrag = () => {
    drag.current.active = false;
  };

  if (!current) return null;

  return (
    <div
      className="modal fade show d-block"
      tabIndex={-1}
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
    >
      <style>{`
        .lb-btn { border:none; background:transparent; color:#4a4a5a;
          width:36px; height:36px; border-radius:8px; display:inline-flex;
          align-items:center; justify-content:center; font-size:17px;
          cursor:pointer; transition:background .15s ease; }
        .lb-btn:hover { background:#f0f0f5; }
        .lb-btn:disabled { opacity:.4; cursor:default; }
        .lb-nav { position:absolute; top:50%; transform:translateY(-50%);
          width:40px; height:40px; border-radius:50%; border:none;
          background:rgba(255,255,255,0.9); box-shadow:0 2px 8px rgba(0,0,0,0.2);
          color:#333; font-size:20px; display:inline-flex; align-items:center;
          justify-content:center; cursor:pointer; z-index:2; transition:background .15s ease; }
        .lb-nav:hover { background:#fff; }
        .lb-prev { left:10px; } .lb-next { right:10px; }
        .lb-zoom { min-width:46px; text-align:center; font-size:13px; color:#6c757d; }
        .lb-sep { width:1px; height:20px; background:#e2e2ea; margin:0 4px; }
        .lb-spinner { width:34px; height:34px; border-radius:50%;
          border:3px solid rgba(0,0,0,0.15); border-top-color:#6c757d;
          animation:lb-spin .8s linear infinite; position:absolute; }
        @keyframes lb-spin { to { transform:rotate(360deg); } }
      `}</style>

      <div
        className="modal-dialog modal-dialog-centered"
        style={{ width: "fit-content", maxWidth: "94vw" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="modal-content border-0 shadow-lg"
          style={{ borderRadius: 14, overflow: "hidden", minWidth: 340 }}
        >
          {/* Header */}
          <div className="modal-header py-2 px-3 align-items-center">
            <div style={{ minWidth: 0 }}>
              <h6 className="mb-0 text-truncate" title={current.name}>
                {current.name}
              </h6>
              <small className="text-muted">
                {images.length > 1 ? `${index + 1} / ${images.length}` : ""}
                {Number(current.size) > 0
                  ? `${images.length > 1 ? " · " : ""}${formatSize(
                      Number(current.size),
                    )}`
                  : ""}
              </small>
            </div>
            <div className="d-flex align-items-center ms-3">
              <button
                className="lb-btn"
                title="Download"
                onClick={() => onDownload(current)}
              >
                <i className="ti ti-download" />
              </button>
              <button className="lb-btn" title="Close (Esc)" onClick={onClose}>
                <i className="ti ti-x" />
              </button>
            </div>
          </div>

          {/* Image stage — sized to the image */}
          <div
            ref={stageRef}
            className="d-flex justify-content-center align-items-center mx-auto"
            onMouseMove={onMouseMove}
            onMouseUp={endDrag}
            onMouseLeave={endDrag}
            style={{
              position: "relative",
              overflow: "hidden",
              background:
                "repeating-conic-gradient(#f4f4f7 0% 25%, #e9e9f0 0% 50%) 50% / 22px 22px",
              ...(dims
                ? { width: dims.w, height: dims.h }
                : { minWidth: 320, minHeight: 220 }),
            }}
          >
            {images.length > 1 && (
              <>
                <button
                  className="lb-nav lb-prev"
                  title="Previous (←)"
                  onClick={() => goTo(index - 1)}
                >
                  <i className="ti ti-chevron-left" />
                </button>
                <button
                  className="lb-nav lb-next"
                  title="Next (→)"
                  onClick={() => goTo(index + 1)}
                >
                  <i className="ti ti-chevron-right" />
                </button>
              </>
            )}

            {!loaded && <div className="lb-spinner" />}
            <img
              src={buildUploadUrl(current.path)}
              alt={current.name}
              draggable={false}
              onLoad={(e) => {
                natural.current = {
                  w: e.currentTarget.naturalWidth,
                  h: e.currentTarget.naturalHeight,
                };
                setLoaded(true);
                computeDims();
              }}
              onMouseDown={onMouseDown}
              onDoubleClick={() => setZoom((z) => (z > 1 ? 1 : 2))}
              onError={(e) => {
                e.currentTarget.src =
                  "assets/img/placeholders/image-fallback.png";
                setLoaded(true);
              }}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                opacity: loaded ? 1 : 0,
                transition: drag.current.active
                  ? "none"
                  : "transform .15s ease, opacity .2s ease",
                transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom}) rotate(${rotation}deg)`,
                cursor:
                  zoom > 1 ? (drag.current.active ? "grabbing" : "grab") : "default",
              }}
            />
          </div>

          {/* Toolbar */}
          <div className="modal-footer py-2 justify-content-center">
            <button
              className="lb-btn"
              title="Zoom out (-)"
              onClick={() => zoomBy(-ZOOM_STEP)}
              disabled={zoom <= MIN_ZOOM}
            >
              <i className="ti ti-zoom-out" />
            </button>
            <span className="lb-zoom">{Math.round(zoom * 100)}%</span>
            <button
              className="lb-btn"
              title="Zoom in (+)"
              onClick={() => zoomBy(ZOOM_STEP)}
              disabled={zoom >= MAX_ZOOM}
            >
              <i className="ti ti-zoom-in" />
            </button>
            <span className="lb-sep" />
            <button
              className="lb-btn"
              title="Rotate left"
              onClick={() => rotateBy(-90)}
            >
              <i className="ti ti-rotate-2" />
            </button>
            <button
              className="lb-btn"
              title="Rotate right"
              onClick={() => rotateBy(90)}
            >
              <i className="ti ti-rotate-clockwise" />
            </button>
            <button className="lb-btn" title="Reset (0)" onClick={reset}>
              <i className="ti ti-aspect-ratio" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageLightbox;
